import { useModalFocus } from '../lib/useModalFocus.js';

export interface ScopeScene {
  id: string;
  sceneNumber: string | null;
  slugline: string | null;
}

/**
 * THIS SCENE, OR EVERY SCENE — asked before anything is written (owner review
 * 2026-10-27, decision D2).
 *
 * Tagging: the element goes on every scene whose text names it as a whole
 * word (`appearsInText`, shared). Un-tagging: it comes off every scene it is
 * on. Either way the producer sees HOW MANY and WHICH before pressing, so a
 * name that turned out to be a common word ("car" in a road movie) is visible
 * as a long list rather than discovered on forty sheets afterwards — and the
 * list shown is exactly the list sent.
 */
export function ElementScopeDialog({
  mode,
  name,
  sourceSceneId,
  scenes,
  busy = false,
  error = null,
  onThisScene,
  onAllScenes,
  onCancel,
}: {
  mode: 'TAG' | 'REMOVE';
  name: string;
  /** The scene the producer was working on — always one of `scenes`. */
  sourceSceneId: string;
  scenes: readonly ScopeScene[];
  busy?: boolean;
  error?: string | null;
  onThisScene: () => void;
  onAllScenes: () => void;
  onCancel: () => void;
}) {
  const modal = useModalFocus<HTMLDivElement>(() => {
    if (!busy) onCancel();
  });

  const count = scenes.length;
  const heading =
    mode === 'TAG'
      ? `“${name}” appears in ${String(count)} scenes`
      : `“${name}” is on ${String(count)} scenes`;
  const allLabel = mode === 'TAG' ? `Tag all ${String(count)} scenes` : `Remove from all ${String(count)} scenes`;

  return (
    <div
      data-testid="element-scope-dialog"
      data-mode={mode}
      onKeyDown={modal.onKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="element-scope-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(35, 35, 35, 0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '5vh 16px',
        overflowY: 'auto',
        zIndex: 40,
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div
        ref={modal.ref}
        style={{
          background: 'var(--color-bg)',
          width: 'min(560px, 100%)',
          border: '1px solid var(--color-divider)',
          boxShadow: '0 18px 48px rgba(16, 24, 40, 0.18)',
        }}
      >
        <header style={{ padding: '18px 22px', borderBottom: '2px solid var(--ink)' }}>
          <h2
            id="element-scope-title"
            data-testid="element-scope-title"
            style={{ margin: 0, fontSize: 18, lineHeight: 1.35, overflowWrap: 'anywhere' }}
          >
            {heading}
          </h2>
          <p className="mm" style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.6 }}>
            {mode === 'TAG'
              ? 'Matched as a whole word in each scene’s text, upper or lower case. Check the list before tagging them all.'
              : 'Take it off only the scene you are on, or off every scene below.'}
          </p>
        </header>

        <ul
          data-testid="element-scope-scenes"
          style={{
            listStyle: 'none',
            margin: 0,
            padding: '12px 22px',
            maxHeight: 260,
            overflowY: 'auto',
            display: 'grid',
            gap: 4,
            fontSize: 13,
          }}
        >
          {scenes.map((s) => (
            <li key={s.id} data-testid={`element-scope-scene-${s.id}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <strong style={{ whiteSpace: 'nowrap' }}>{`Sc ${s.sceneNumber ?? '—'}`}</strong>
              <span className="mm" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                {s.slugline ?? 'Untitled scene'}
              </span>
              {s.id === sourceSceneId ? (
                <span className="mm" style={{ fontSize: 11.5, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                  this scene
                </span>
              ) : null}
            </li>
          ))}
        </ul>

        {error === null ? null : (
          <p data-testid="element-scope-error" role="alert" style={{ margin: '0 22px 12px', fontSize: 13, color: 'var(--danger)' }}>
            {error}
          </p>
        )}

        <footer
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            padding: '14px 22px',
            borderTop: '1px solid var(--color-divider)',
            background: 'var(--color-surface)',
          }}
        >
          <button type="button" className="btn btn-ghost" data-testid="element-scope-cancel" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            data-testid="element-scope-this"
            disabled={busy}
            onClick={onThisScene}
            style={{ marginLeft: 'auto' }}
          >
            Only this scene
          </button>
          <button type="button" className="btn btn-primary" data-testid="element-scope-all" disabled={busy} onClick={onAllScenes}>
            {busy ? 'Saving…' : allLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
